import { Col, Row } from "react-bootstrap";
import { AiOutlineLeft, AiOutlineRight } from "react-icons/ai";

interface SessionPaginationProps {
    activePage: number;
    totalSessions: number;
    sessionsPerPage: number;
    onPageChange: (page: number) => void;
}

export default function SessionPagination({ activePage, totalSessions, sessionsPerPage, onPageChange }: SessionPaginationProps) {
    const totalPages = Math.ceil(totalSessions / sessionsPerPage);

    // Function to determine which page numbers to display
    const getPageNumbers = (currentPage: number, totalPages: number) => {
        const pageNumbers: (number | string)[] = [];
        
        // Always show first page
        pageNumbers.push(1);
        
        // Define range around current page
        const rangeStart = Math.max(2, currentPage - 2);
        const rangeEnd = Math.min(totalPages - 1, currentPage + 2);
        
        // Add ellipsis after first page if needed
        if (rangeStart > 2) {
            pageNumbers.push('ellipsis-1');
        }
        
        // Add page numbers around current page
        for (let i = rangeStart; i <= rangeEnd; i++) {
            pageNumbers.push(i);
        }
        
        // Add ellipsis before last page if needed
        if (rangeEnd < totalPages - 1) {
            pageNumbers.push('ellipsis-2');
        }
        
        // Always show last page if it exists
        if (totalPages > 1) {
            pageNumbers.push(totalPages);
        }
        
        return pageNumbers;
    };

    // Get the page numbers to display
    const pageNumbers = getPageNumbers(activePage, totalPages);

    // Map the page numbers to components
    const pages = pageNumbers.map((page, index) => {
        if (typeof page === 'string' && page.startsWith('ellipsis')) {
            return <Col key={page} className="session-pagination me-4">...</Col>;
        }
        
        const isActive = page === activePage;
        return (
            <Col 
                key={`page-${page}`} 
                className={isActive ? "session-pagination-active me-4" : "session-pagination me-4"}
                onClick={() => !isActive && onPageChange(Number(page))}
                style={{ cursor: !isActive ? 'pointer' : 'default' }}
            >
                {page}
            </Col>
        );
    });

    return (
        <Row className="d-flex align-items-center">
            <Col 
                className="session-pagination-arrow pe-4" 
                onClick={() => activePage > 1 && onPageChange(activePage - 1)}
                style={{ 
                    cursor: activePage > 1 ? 'pointer' : 'default',
                    opacity: activePage > 1 ? 1 : 0.5
                }}
            >
                <AiOutlineLeft />
            </Col>
            {pages}
            <Col 
                className="session-pagination-arrow" 
                onClick={() => activePage < totalPages && onPageChange(activePage + 1)}
                style={{ 
                    cursor: activePage < totalPages ? 'pointer' : 'default',
                    opacity: activePage < totalPages ? 1 : 0.5
                }}
            >
                <AiOutlineRight />
            </Col>
        </Row>
    );
}
